import { useEffect, useRef } from 'react';

/** A background poll is not read evidence. A visible latest message is. */
export function useVisibleConversationRead(
  squadId: number,
  messageId: string | undefined,
  refreshRevision: number,
  acknowledge: (messageId: string) => Promise<void>,
) {
  const messageRef = useRef<HTMLDivElement>(null);
  const acknowledgedRef = useRef<string | null>(null);
  useEffect(() => {
    const element = messageRef.current;
    if (!element || !messageId || !/^[1-9]\d*$/.test(messageId)) return;
    const key = `${squadId}:${messageId}`;
    if (acknowledgedRef.current === key) return;
    let active = true;
    let visible = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const canRead = () => active && visible && document.visibilityState === 'visible' && document.hasFocus();
    const evaluate = () => {
      clearTimeout(timer);
      if (!canRead() || inFlight || acknowledgedRef.current === key) return;
      timer = setTimeout(() => {
        if (!canRead()) return;
        inFlight = true;
        void acknowledge(messageId).then(() => {
          if (active) acknowledgedRef.current = key;
        }).catch(() => {
          // A changed boundary or network failure retries on the existing message poll.
        }).finally(() => { inFlight = false; });
      }, 400);
    };
    const observer = new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.target === element && entry.isIntersecting && entry.intersectionRatio >= 0.5);
      evaluate();
    }, { threshold: 0.5 });
    observer.observe(element);
    document.addEventListener('visibilitychange', evaluate);
    window.addEventListener('focus', evaluate);
    window.addEventListener('blur', evaluate);
    return () => {
      active = false; clearTimeout(timer); observer.disconnect();
      document.removeEventListener('visibilitychange', evaluate);
      window.removeEventListener('focus', evaluate);
      window.removeEventListener('blur', evaluate);
    };
  }, [squadId, messageId, refreshRevision, acknowledge]);
  return messageRef;
}
